import type { Settings as ProLayoutSettings } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, setLocale, useAntdConfigSetter } from '@umijs/max';
import {
  type LayoutSettings as CoreLayoutSettings,
  type CoreSessionConfig,
  type CurrentUser,
  SessionProvider,
  useSession,
  useShell,
  useTheme,
} from '@zhiyue/core';
import '@zhiyue/core/style.css';
import '@zhiyue/task-management/style.css';

import { BizAppConfig } from 'config/application.manifest';
import {
  getBrandAppName,
  getStoredBrandLocale,
  syncBrandDocument,
} from 'config/brand';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/ja';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/zh-tw';
import React from 'react';
import accessFactory from '@/access';
import {
  getBootstrapTenantList,
  sessionAdapter,
} from '@/adapters/sessionAdapter';
import { getSessionAppearanceConfig } from '@/adapters/sessionAppearance';
import { readCurrentTenantUuid } from '@/adapters/tenantStorage';
import {
  AvatarDropdown,
  Footer,
  NotificationIcon,
  ThemeToggle,
} from '@/components';
import {
  SettingsButton,
  SettingsNavigation,
} from '@/components/Layout/SettingsNavigation';
import SiderResizeHandle, {
  DEFAULT_SIDER_WIDTH,
  getStoredSiderWidth,
} from '@/components/Layout/SiderResizeHandle';
import PlatformThemeBoundary, {
  getApplicationTheme,
  getNavigationToken,
} from '@/components/PlatformTheme';
import { ContactDetailDrawerProvider } from '@/modules/zy-crm/components/contact-detail-drawer';
import { CustomerDetailDrawerProvider } from '@/modules/zy-crm/components/customer-detail-drawer';
import CrmGlobalSearch from '@/modules/zy-crm/components/global-search';
import { LeadDetailDrawerProvider } from '@/modules/zy-crm/components/lead-detail-drawer';
import { MarketingCampaignDetailDrawerProvider } from '@/modules/zy-crm/components/marketing-campaign-detail-drawer';
import { OpportunityDetailDrawerProvider } from '@/modules/zy-crm/components/opportunity-detail-drawer';
import { MemberDirectoryProvider } from '@/modules/zy-crm/member-directory';
import {
  buildNavigation,
  canUseProductCenter,
  getSelectedMenuKeys,
  getSettingsItems,
} from '@/navigation';
import {
  type AuthorizedResourceAccessMap,
  buildAuthorizedResourceAccessMap,
} from '@/permissions/adapter';
import { getMyAuthorizedResources } from '@/services/bff';
import {
  clearAutoRefresh,
  consumeApplicationAccessReturnTo,
  ensureValidAccessToken,
  hasOAuth2Session,
  isSSORedirectExemptPath,
  redirectToSSO,
  setupAutoRefresh,
} from '@/services/oauth2';
import {
  getLayoutVisibility,
  updateSessionSnapshot,
} from '@/state/sessionSnapshot';
import { buildCrmMenuData } from '@/utils/menuData';
import { getRuntimeDateLocale } from '@/utils/runtimeDateLocale';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';

export const request: RequestConfig = {
  ...errorConfig,
};

const getSettingsWithSiderWidth = (locale?: string) => ({
  ...defaultSettings,
  title: getBrandAppName(locale),
  siderWidth: getStoredSiderWidth(),
});

const getUserSyncKey = (
  user?:
    | (Partial<CurrentUser> & {
        uuid?: string;
      })
    | null,
) => {
  if (!user) {
    return '';
  }

  return [user.id, user.uuid, user.username, user.tenantUuid]
    .filter(Boolean)
    .join('|');
};

const getSessionConfig = (): CoreSessionConfig => {
  return {
    redirectAfterLogout: '/user/login',
    ...getSessionAppearanceConfig(),
    adapter: sessionAdapter,
    ui: {
      links: {
        help: 'https://docs.shanhuyun.cn/',
      },
      userMenu: {
        items: [],
      },
      locales: [
        { key: 'en-US', label: 'English', icon: '🇺🇸', title: 'Language' },
        { key: 'ja-JP', label: '日本語', icon: '🇯🇵', title: '言語' },
        { key: 'zh-CN', label: '简体中文', icon: '🇨🇳', title: '语言' },
        { key: 'zh-TW', label: '繁體中文', icon: '🇭🇰', title: '語言' },
      ],
    },
    featureFlags: {
      showHeader: true,
      showNav: true,
      showFooter: true,
      enableTenantSwitch: true,
      enableRegister: false,
    },
    onTenantSwitched: () => {
      const returnTo = consumeApplicationAccessReturnTo();
      window.location.replace(returnTo || '/');
    },
    onLocaleChanged: (locale) => {
      setLocale(locale, false);
    },
    onUnauthorized: () => {
      if (!isSSORedirectExemptPath(history.location.pathname)) {
        redirectToSSO();
      }
    },
  };
};

const SessionSync: React.FC<{ setInitialState: (updater: any) => void }> = ({
  setInitialState,
}) => {
  const { navTheme } = useTheme();
  const { currentUser, loading } = useSession();
  const { shellConfig } = useShell();
  const setAntdConfig = useAntdConfigSetter();
  const [runtimeLocale, setRuntimeLocale] = React.useState(
    getStoredBrandLocale(),
  );
  const themeSyncedRef = React.useRef<CoreLayoutSettings['navTheme'] | null>(
    null,
  );
  const localeSyncedRef = React.useRef<string | null>(null);
  const titleSyncedRef = React.useRef<string | null>(null);
  const syncedRef = React.useRef({
    navTheme: '',
    userSyncKey: '',
  });
  const userSyncKey = React.useMemo(
    () => getUserSyncKey(currentUser),
    [currentUser],
  );

  React.useEffect(() => {
    updateSessionSnapshot({
      currentUser,
      loading,
      featureFlags: shellConfig.featureFlags,
    });

    if (typeof setInitialState !== 'function') {
      return;
    }

    const lastSynced = syncedRef.current;
    if (
      lastSynced.navTheme === navTheme &&
      lastSynced.userSyncKey === userSyncKey
    ) {
      return;
    }

    setInitialState((prev: any) => {
      if (!prev) return prev;
      const prevUserSyncKey = getUserSyncKey(prev.currentUser);
      if (
        prev.settings?.navTheme === navTheme &&
        prevUserSyncKey === userSyncKey
      ) {
        return prev;
      }

      return {
        ...prev,
        currentUser,
        settings: {
          ...prev.settings,
          navTheme,
        },
      };
    });

    syncedRef.current = {
      navTheme,
      userSyncKey,
    };
  }, [
    currentUser,
    loading,
    navTheme,
    setInitialState,
    shellConfig.featureFlags,
    userSyncKey,
  ]);

  React.useEffect(() => {
    if (loading) {
      return;
    }

    if (currentUser && hasOAuth2Session()) {
      setupAutoRefresh();
      return;
    }

    clearAutoRefresh();
  }, [currentUser, loading]);

  React.useEffect(() => {
    if (themeSyncedRef.current === navTheme) {
      return;
    }
    themeSyncedRef.current = navTheme;

    const useDarkMode = navTheme === 'realDark';

    document.documentElement.dataset.theme = useDarkMode ? 'dark' : 'light';
    setAntdConfig((prevConfig) => ({
      ...prevConfig,
      theme: {
        ...prevConfig?.theme,
        ...getApplicationTheme(useDarkMode),
      },
    }));
  }, [navTheme, setAntdConfig]);

  React.useEffect(() => {
    if (localeSyncedRef.current === runtimeLocale) {
      return;
    }
    localeSyncedRef.current = runtimeLocale;

    const { antdLocale, dayjsLocale } = getRuntimeDateLocale(runtimeLocale);

    dayjs.locale(dayjsLocale);
    setAntdConfig((prevConfig) => {
      if (prevConfig?.locale === antdLocale) {
        return prevConfig;
      }

      return {
        ...prevConfig,
        locale: antdLocale,
      };
    });
  }, [runtimeLocale, setAntdConfig]);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const syncLocale = () => {
      setRuntimeLocale(getStoredBrandLocale());
    };

    syncLocale();
    window.addEventListener('languagechange', syncLocale);
    window.addEventListener('storage', syncLocale);

    return () => {
      window.removeEventListener('languagechange', syncLocale);
      window.removeEventListener('storage', syncLocale);
    };
  }, []);

  React.useEffect(() => {
    const nextTitle = syncBrandDocument(runtimeLocale);

    if (titleSyncedRef.current === nextTitle) {
      return;
    }
    titleSyncedRef.current = nextTitle;

    if (typeof setInitialState !== 'function') {
      return;
    }

    setInitialState((prev: any) => {
      if (!prev) return prev;

      const prevSettings = prev.settings || {};
      if (prevSettings.title === nextTitle) {
        return prev;
      }

      return {
        ...prev,
        settings: {
          ...prevSettings,
          title: nextTitle,
        },
      };
    });
  }, [runtimeLocale, setInitialState]);

  return null;
};

// 必须用 innerProvider 而不是 rootContainer：umi 的包装顺序是
// innerProvider → i18nProvider → accessProvider → dataflowProvider → … → rootContainer，
// rootContainer 在最外层，其子树拿不到 AccessContext（useAccess 返回 null）
// 与 plugin-model 上下文，全局抽屉里的权限按钮会因此永远隐藏。
export const innerProvider = (container: React.ReactNode) => (
  <RootSessionContainer>{container}</RootSessionContainer>
);

const RootSessionContainer: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const sessionConfig = React.useMemo(() => getSessionConfig(), []);

  return (
    <SessionProvider config={sessionConfig}>
      <PlatformThemeBoundary>
        <MemberDirectoryProvider>
          <ContactDetailDrawerProvider>
            <CustomerDetailDrawerProvider>
              <OpportunityDetailDrawerProvider>
                <LeadDetailDrawerProvider>
                  <MarketingCampaignDetailDrawerProvider>
                    {children}
                  </MarketingCampaignDetailDrawerProvider>
                </LeadDetailDrawerProvider>
              </OpportunityDetailDrawerProvider>
            </CustomerDetailDrawerProvider>
          </ContactDetailDrawerProvider>
        </MemberDirectoryProvider>
      </PlatformThemeBoundary>
    </SessionProvider>
  );
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<ProLayoutSettings & CoreLayoutSettings>;
  currentUser?: API.CurrentUser & CurrentUser;
  authorizedResourceAccess?: AuthorizedResourceAccessMap;
  currentTenantIsAdmin?: boolean;
  loading?: boolean;
}> {
  let authorizedResourceAccess: AuthorizedResourceAccessMap = {};
  let currentTenantIsAdmin = false;

  if (hasOAuth2Session()) {
    const [authorizedResourcesResult, tenantListResult] =
      await Promise.allSettled([
        getMyAuthorizedResources(BizAppConfig.appSsoClientId || ''),
        getBootstrapTenantList(),
      ]);

    if (authorizedResourcesResult.status === 'fulfilled') {
      authorizedResourceAccess = buildAuthorizedResourceAccessMap(
        authorizedResourcesResult.value,
      );
    }

    if (tenantListResult.status === 'fulfilled') {
      const tenantList = tenantListResult.value ?? [];
      const currentTenantUuid = readCurrentTenantUuid() || tenantList[0]?.id;
      currentTenantIsAdmin = tenantList.some(
        (tenant) =>
          tenant.id === currentTenantUuid && tenant.roleLabel === '管理员',
      );
    }
  }

  return {
    loading: true,
    authorizedResourceAccess,
    currentTenantIsAdmin,
    settings: getSettingsWithSiderWidth(getStoredBrandLocale()) as Partial<
      ProLayoutSettings & CoreLayoutSettings
    >,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const getCurrentLayoutVisibility = () =>
    getLayoutVisibility(history.location.pathname);
  const siderWidth =
    (
      initialState?.settings as Partial<CoreLayoutSettings> & {
        siderWidth?: number;
      }
    )?.siderWidth ?? DEFAULT_SIDER_WIDTH;

  const resizeEnabled = !(
    initialState?.settings as { collapsed?: boolean } | undefined
  )?.collapsed;

  const handleResizeWidth = React.useCallback(
    (nextWidth: number) => {
      setInitialState((prev) => {
        if (!prev) return prev;
        const prevSettings =
          (prev.settings as Partial<CoreLayoutSettings> & {
            siderWidth?: number;
          }) || {};
        if (prevSettings.siderWidth === nextWidth) {
          return prev;
        }
        return {
          ...prev,
          settings: {
            ...prevSettings,
            siderWidth: nextWidth,
          },
        };
      });
    },
    [setInitialState],
  );

  return {
    ...initialState?.settings,
    className: 'crm-shell',
    selectedKeys: getSelectedMenuKeys(
      history.location.pathname,
      buildNavigation(
        accessFactory(initialState),
        canUseProductCenter(initialState?.currentUser),
      ),
      new URLSearchParams(history.location.search).get('from') || undefined,
    ),
    layout: 'mix',
    splitMenus: true,
    fixedHeader: true,
    fixSiderbar: true,
    token: getNavigationToken(initialState?.settings?.navTheme !== 'light'),
    menu: { defaultOpenAll: true, ignoreFlatMenu: true, autoClose: false },
    contentStyle: {
      height: 'calc(100dvh - 44px)',
      overflow: 'auto',
      minWidth: 0,
      padding: 0,
    },
    breadcrumbRender: false,
    rightContentRender: false,
    actionsRender: () => {
      const { showHeader } = getCurrentLayoutVisibility();
      if (!showHeader) {
        return [];
      }

      return [
        <CrmGlobalSearch key="crmGlobalSearch" />,
        <SettingsButton key="settings" />,
        <NotificationIcon key="notification" />,
        <ThemeToggle key="themeToggle" />,
      ];
    },
    avatarProps: {
      render: () => {
        const { showHeader } = getCurrentLayoutVisibility();
        if (!showHeader) {
          return null;
        }
        return <AvatarDropdown />;
      },
    },
    waterMarkProps: {
      content: '',
    },
    footerRender: () => {
      const { showFooter } = getCurrentLayoutVisibility();
      if (!showFooter) {
        return false;
      }
      return <Footer />;
    },
    headerRender: (_props, dom) => {
      const { showHeader } = getCurrentLayoutVisibility();
      if (!showHeader) return false;
      if (!React.isValidElement(dom)) return dom;
      return React.cloneElement(
        dom as React.ReactElement<{ menuData?: any[] }>,
        {
          menuData: (_props.menuData || []).filter(
            (item: any) => item.key !== '/setting',
          ),
        },
      );
    },
    menuRender: (_props, dom) =>
      getCurrentLayoutVisibility().showNav ? dom : false,
    menuContentRender: (_props, dom) =>
      history.location.pathname.startsWith('/setting') ? (
        <SettingsNavigation
          collapsed={Boolean(_props.collapsed && !_props.isMobile)}
        />
      ) : (
        <div className="crm-sider-menu-content" key={history.location.pathname}>
          {dom}
        </div>
      ),
    menuDataRender: () => {
      const access = accessFactory(initialState);
      const menus = buildNavigation(
        access,
        canUseProductCenter(initialState?.currentUser),
      );
      const settingGroups = getSettingsItems(access);
      if (settingGroups.length)
        menus.push({
          key: '/setting',
          path: '/setting',
          name: '设置',
          children: settingGroups,
        });
      return buildCrmMenuData(menus);
    },
    headerTitleRender: undefined,
    onPageChange: () => {
      const { pathname } = history.location;
      if (isSSORedirectExemptPath(pathname)) {
        return;
      }

      void ensureValidAccessToken();
    },
    menuHeaderRender: undefined,
    siderWidth,
    childrenRender: (children) => {
      const { showNav } = getCurrentLayoutVisibility();
      return (
        <>
          <SessionSync setInitialState={setInitialState} />
          <PlatformThemeBoundary>{children}</PlatformThemeBoundary>
          <SiderResizeHandle
            enabled={showNav && resizeEnabled}
            siderWidth={siderWidth}
            onResizeWidth={handleResizeWidth}
          />
        </>
      );
    },
  };
};
